import type { Logger } from '@interopio/gateway/logging/api';
import { AsyncLocalStorage } from 'node:async_hooks';
import type { Middleware, ServerHttpRequest, ServerHttpResponse, ServerWebExchange } from '../../../types/web/server';
import { DefaultWebExchange } from '../../server/exchange.ts';
import type { HttpHeaders } from '../../../types/web/http';
import { HttpStatus } from '../../http/status.ts';
import getLogger from '../../logger.ts';
import { checkAndLogClientDisconnectedError } from '../util/client.ts';
import type { WebErrorHandler, WebFilter, WebHandler } from './types.ts';
import { ErrorHandlingWebHandler, FilteringWebHandler } from './handler.ts';
import type { HttpHandler } from '../../http/server/types.ts';
import { ServerCodecConfigurer } from '../../http/codec/types.ts';
import codecConfig from '../../http/codec/config.ts';
import { LoggingCodecSupport } from '../../http/codec/core.ts';

class HttpWebHandlerAdapter {

    readonly #logger: Logger;

    #codecConfigurer: ServerCodecConfigurer = undefined!;
    #enableLoggingRequestDetails: boolean = false;
    readonly #delegate: WebHandler;
    #storage?: AsyncLocalStorage<{ exchange: ServerWebExchange }>;

    constructor(logger: Logger, delegate: WebHandler) {
        this.#logger = logger;
        this.#delegate = delegate;
    }

    get codecConfigurer(): ServerCodecConfigurer {
        if (this.#codecConfigurer === undefined) {
            this.codecConfigurer = codecConfig.serverConfigurer.create()
        }
        return this.#codecConfigurer;
    }

    set codecConfigurer(configurer: ServerCodecConfigurer) {
        if (configurer === null || configurer === undefined) {
            throw new Error('codecConfigurer must not be null or undefined');
        }
        this.#codecConfigurer = configurer;

        this.#enableLoggingRequestDetails = false;
        for (const reader of this.#codecConfigurer.readers) {
            if (reader instanceof LoggingCodecSupport && reader.enableLoggingRequestDetails) {
                this.#enableLoggingRequestDetails = true;
                break;
            }
        }
    }

    protected createExchange(request: ServerHttpRequest, response: ServerHttpResponse): ServerWebExchange {
        const exchange = new DefaultWebExchange(request, response, this.codecConfigurer);
        return exchange;
    }

    set storage(storage: AsyncLocalStorage<{ exchange: ServerWebExchange }>) {
        this.#storage = storage;
    }


    formatHeaders(headers: HttpHeaders): string {
        let result = '{';

        for (const key of headers.keys()) {
            if (!this.#enableLoggingRequestDetails) {
                result += 'masked, ';
                break;
            }
            else {
                const value = headers.get(key);
                result += `"${key}": "${value}", `;
            }
        }
        if (result.endsWith(', ')) {
            result = result.slice(0, -2);
        }
        result += '}';
        return result;

    }

    formatRequest(request: ServerHttpRequest): string {
        const query = request.URL.search;
        return `HTTP ${request.method} "${request.path}${query}`;
    }

    logRequest(exchange: ServerWebExchange) {
        if (this.#logger.enabledFor('debug')) {
            const trace = this.#logger.enabledFor('trace');
            this.#logger.debug(`${exchange.logPrefix}${this.formatRequest(exchange.request)}${trace ? `, headers: ${this.formatHeaders(exchange.request.headers)}` : ''}"`);

        }
    }

    logResponse(exchange: ServerWebExchange) {
        if (this.#logger.enabledFor('debug')) {
            const trace = this.#logger.enabledFor('trace');
            const status = exchange.response.statusCode;
            this.#logger.debug(`${exchange.logPrefix}Completed ${status ?? '200 OK'}${trace ? `, headers: ${this.formatHeaders(exchange.response.headers)}` : ''}"`);
        }
    }

    handleUnresolvedError(exchange: ServerWebExchange, error: Error) {
        const { request, response, logPrefix } = exchange;

        if (response.setStatusCode(HttpStatus.INTERNAL_SERVER_ERROR)) {
            this.#logger.error(`${logPrefix}500 Server Error for ${this.formatRequest(request)}`, error);
            return;
        }
        else if (checkAndLogClientDisconnectedError(error, this.#logger)) {
            // todo notify observers
            return;
        }
        else {

            this.#logger.error(`${logPrefix}Error [${error.message} for ${this.formatRequest(request)}, but already ended (${response.statusCode})`, error);
            throw error;
        }

    }

    async web(exchange: ServerWebExchange): Promise<void> {
        return await this.#delegate.handle(exchange);
    }

    async http(request: ServerHttpRequest, response: ServerHttpResponse): Promise<void> {

        const exchange = this.createExchange(request, response);
        const callback = () => {
            this.logRequest(exchange);
            return this.web(exchange)
                .then(() => {
                    this.logResponse(exchange);
                })
                .catch((error: Error) => {
                    this.handleUnresolvedError(exchange, error)
                })
                .then(async () => {
                    await exchange.response.end();
                });
        };

        await new Promise<void>((resolve, reject) => {
            if (this.#storage !== undefined) {
                this.#storage.run({ exchange }, () => {
                    callback().then(() => resolve()).catch((error) => reject(error));
                });
            }
            else {
                callback().then(() => resolve()).catch((error) => reject(error));
            }
        });
    }
}

export class WebHttpHandlerBuilder {
    readonly #webHandler: WebHandler;
    readonly #filters: WebFilter[] = [];
    readonly #exceptionHandlers: WebErrorHandler[] = [];
    readonly #middleware: Middleware = [];
    #handlerDecorator?: (handler: HttpHandler) => HttpHandler;
    #storage?: AsyncLocalStorage<{ exchange: ServerWebExchange }> = new AsyncLocalStorage();
    #codecConfigurer?: ServerCodecConfigurer;

    constructor(webHandler: WebHandler) {
        this.#webHandler = webHandler;
    }

    static webHandler(webHandler: WebHandler): WebHttpHandlerBuilder {
        return new WebHttpHandlerBuilder(webHandler);
    }

    filter(...filters: WebFilter[]): this {
        if (filters) {
            this.#filters.push(...filters);
        }
        return this;
    }

    middleware(consumer: (middleware: Middleware) => void): this {
        consumer(this.#middleware);
        return this;
    }

    exceptionHandler(...handlers: WebErrorHandler[]): this {
        if (handlers) {
            this.#exceptionHandlers.push(...handlers);
        }
        return this;
    }

    exceptionHandlers(consumer: (handlers: WebErrorHandler[]) => void): this {
        consumer(this.#exceptionHandlers);
        return this;
    }

    storage(storage: AsyncLocalStorage<{ exchange: ServerWebExchange }>): this {
        this.#storage = storage;
        return this;
    }

    codecConfigurer(configurer: ServerCodecConfigurer): this {
        this.#codecConfigurer = configurer;
        return this;
    }

    hasCodecConfigurer(): boolean {
        return this.#codecConfigurer !== undefined;
    }

    httpHandlerDecorator(decorator: (handler: HttpHandler) => HttpHandler): this {
        if (this.#handlerDecorator === undefined) {
            this.#handlerDecorator = decorator;
        }
        else {
            const previousDecorator = this.#handlerDecorator;
            this.#handlerDecorator = (handler: HttpHandler) => {
                handler = decorator(handler);
                handler = previousDecorator(handler);
                return handler;
            }
        }
        return this;
    }

    hasHttpHandlerDecorator(): boolean {
        return this.#handlerDecorator !== undefined;
    }

    build(): HttpHandler {
        const logger = getLogger('http');
        let decorated: WebHandler = new FilteringWebHandler(this.#webHandler, ...this.#filters);
        decorated = new ErrorHandlingWebHandler(decorated, ...this.#exceptionHandlers);

        const adapter = new HttpWebHandlerAdapter(logger, decorated);
        if (this.#storage !== undefined) adapter.storage = this.#storage;
        if (this.#codecConfigurer !== undefined) {
            adapter.codecConfigurer = this.#codecConfigurer;
        }
        const adapted: HttpHandler = async (request, response) => adapter.http(request, response);


        return this.#handlerDecorator ? this.#handlerDecorator(adapted) : adapted;
    }
}
